import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLinkIcon, TrendingUpIcon, TrendingDownIcon, CheckIcon, XIcon, ArrowLeftIcon, RefreshCwIcon } from 'lucide-react';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { ReviewCard } from '../components/ReviewCard';
import { Button } from '../components/Button';
import { SocialShare } from '../components/SocialShare';
import { coinGeckoService } from '../services/coingecko';
import { transformCoinGeckoCrypto } from '../services/coingeckoTransformer';
import { Cryptocurrency } from '../types';
import { cryptocurrencies } from '../data/mockData';
export function CryptoDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [crypto, setCrypto] = useState<Cryptocurrency | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const fetchCryptoData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      console.log(`[CryptoDetail] Fetching data for ${id}...`);
      // Fetch detailed data from CoinGecko
      const detailData = await coinGeckoService.getCryptocurrencyDetails(id);
      // Transform to our format
      const transformed: Cryptocurrency = {
        id: detailData.id,
        name: detailData.name,
        symbol: detailData.symbol.toUpperCase(),
        rank: detailData.market_cap_rank || 0,
        logo: detailData.image?.large || detailData.image?.small || '',
        trustScore: calculateTrustScore(detailData),
        price: detailData.market_data?.current_price?.usd || 0,
        marketCap: detailData.market_data?.market_cap?.usd || 0,
        volume24h: detailData.market_data?.total_volume?.usd || 0,
        change24h: detailData.market_data?.price_change_percentage_24h || 0,
        description: detailData.description?.en || 'No description available.',
        website: detailData.links?.homepage?.[0] || '',
        whitepaper: detailData.links?.whitepaper || '',
        reviews: [],
        pros: generatePros(detailData),
        cons: generateCons(detailData)
      };
      console.log(`[CryptoDetail] Successfully fetched ${transformed.name}`);
      setCrypto(transformed);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching crypto details:', err);
      // Fallback to mock data
      const mockCrypto = cryptocurrencies.find(c => c.id === id);
      if (mockCrypto) {
        setCrypto(mockCrypto);
      }
    } finally {
      setLoading(false);
    }
  };
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCryptoData();
    setRefreshing(false);
  };
  useEffect(() => {
    fetchCryptoData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchCryptoData, 60000);
    return () => clearInterval(interval);
  }, [id]);
  if (loading && !crypto) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-341">
        <div className="text-center" data-id="element-342">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-343"></div>
          <p className="text-slate-600" data-id="element-344">Loading cryptocurrency data...</p>
        </div>
      </div>;
  }
  if (!crypto) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-345">
        <div className="text-center" data-id="element-346">
          <h1 className="text-2xl font-bold mb-4" data-id="element-347">Cryptocurrency not found</h1>
          <Link to="/cryptocurrencies" data-id="element-348">
            <Button data-id="element-349">Back to Cryptocurrencies</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-350">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-351">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-352">
          <div className="flex items-center justify-between mb-6" data-id="element-353">
            <Link to="/cryptocurrencies" className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-id="element-354">
              <ArrowLeftIcon size={20} data-id="element-355" />
              <span data-id="element-356">Back to Cryptocurrencies</span>
            </Link>
            <div className="flex items-center gap-3" data-id="element-357">
              <button onClick={handleRefresh} disabled={refreshing} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50" data-id="element-358">
                <RefreshCwIcon size={18} className={refreshing ? 'animate-spin' : ''} data-id="element-359" />
                <span className="text-sm font-medium" data-id="element-360">Refresh</span>
              </button>
              <SocialShare title={`${crypto.name} (${crypto.symbol}) - Live Price & Analysis`} description={`Check out ${crypto.name} live price, market cap, and comprehensive analysis on CryptoTopReviews`} hashtags={['crypto', crypto.symbol.toLowerCase(), crypto.name.toLowerCase().replace(/\s+/g, '')]} data-id="element-361" />
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-362">
            <div className="flex items-center gap-4" data-id="element-363">
              <img src={crypto.logo} alt={crypto.name} className="w-16 h-16 rounded-full bg-white/10 p-2" data-id="element-364" />
              <div data-id="element-365">
                <h1 className="text-4xl font-bold mb-2" data-id="element-366">{crypto.name}</h1>
                <div className="flex items-center gap-3" data-id="element-367">
                  <span className="text-slate-300 text-lg" data-id="element-368">
                    {crypto.symbol}
                  </span>
                  <TrustScore score={crypto.trustScore} size="md" data-id="element-369" />
                </div>
              </div>
            </div>

            <div className="text-right" data-id="element-370">
              <div className="text-3xl font-bold mb-2" data-id="element-371">
                $
                {crypto.price >= 1 ? crypto.price.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) : crypto.price.toFixed(6)}
              </div>
              <div className={`flex items-center justify-end gap-1 text-lg font-semibold ${crypto.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-372">
                {crypto.change24h >= 0 ? <TrendingUpIcon size={20} data-id="element-373" /> : <TrendingDownIcon size={20} data-id="element-374" />}
                <span data-id="element-375">
                  {crypto.change24h >= 0 ? '+' : ''}
                  {crypto.change24h.toFixed(2)}%
                </span>
              </div>
              <p className="text-slate-400 text-xs mt-1" data-id="element-376">
                Updated: {lastUpdated.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-377">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-378">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-379">
            {/* Key Metrics */}
            <Card className="p-6" data-id="element-380">
              <h2 className="text-2xl font-bold mb-6" data-id="element-381">Key Metrics</h2>
              <div className="grid grid-cols-2 gap-6" data-id="element-382">
                <div data-id="element-383">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-384">Market Cap</div>
                  <div className="text-xl font-semibold" data-id="element-385">
                    ${(crypto.marketCap / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-386">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-387">24h Volume</div>
                  <div className="text-xl font-semibold" data-id="element-388">
                    ${(crypto.volume24h / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-389">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-390">Rank</div>
                  <div className="text-xl font-semibold" data-id="element-391">#{crypto.rank}</div>
                </div>
                <div data-id="element-392">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-393">Trust Score</div>
                  <div className="text-xl font-semibold" data-id="element-394">
                    {crypto.trustScore.toFixed(1)}/10
                  </div>
                </div>
              </div>
            </Card>

            {/* About */}
            <Card className="p-6" data-id="element-395">
              <h2 className="text-2xl font-bold mb-4" data-id="element-396">About {crypto.name}</h2>
              <div className="text-slate-700 leading-relaxed mb-6 prose prose-sm max-w-none" dangerouslySetInnerHTML={{
              __html: crypto.description
            }} data-id="element-397" />

              {(crypto.website || crypto.whitepaper) && <div className="flex flex-wrap gap-3" data-id="element-398">
                  {crypto.website && <a href={crypto.website} target="_blank" rel="noopener noreferrer" data-id="element-399">
                      <Button variant="outline" className="flex items-center gap-2" data-id="element-400">
                        <span data-id="element-401">Official Website</span>
                        <ExternalLinkIcon size={16} data-id="element-402" />
                      </Button>
                    </a>}
                  {crypto.whitepaper && <a href={crypto.whitepaper} target="_blank" rel="noopener noreferrer" data-id="element-403">
                      <Button variant="outline" className="flex items-center gap-2" data-id="element-404">
                        <span data-id="element-405">Whitepaper</span>
                        <ExternalLinkIcon size={16} data-id="element-406" />
                      </Button>
                    </a>}
                </div>}
            </Card>

            {/* Pros & Cons */}
            {(crypto.pros.length > 0 || crypto.cons.length > 0) && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-407">
                {crypto.pros.length > 0 && <Card className="p-6" data-id="element-408">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-409">
                      <CheckIcon className="text-green-600" size={24} data-id="element-410" />
                      <span data-id="element-411">Pros</span>
                    </h3>
                    <ul className="space-y-2" data-id="element-412">
                      {crypto.pros.map((pro, index) => <li key={index} className="flex items-start gap-2" data-id="element-413">
                          <CheckIcon className="text-green-600 flex-shrink-0 mt-0.5" size={16} data-id="element-414" />
                          <span className="text-slate-700" data-id="element-415">{pro}</span>
                        </li>)}
                    </ul>
                  </Card>}

                {crypto.cons.length > 0 && <Card className="p-6" data-id="element-416">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-417">
                      <XIcon className="text-red-600" size={24} data-id="element-418" />
                      <span data-id="element-419">Cons</span>
                    </h3>
                    <ul className="space-y-2" data-id="element-420">
                      {crypto.cons.map((con, index) => <li key={index} className="flex items-start gap-2" data-id="element-421">
                          <XIcon className="text-red-600 flex-shrink-0 mt-0.5" size={16} data-id="element-422" />
                          <span className="text-slate-700" data-id="element-423">{con}</span>
                        </li>)}
                    </ul>
                  </Card>}
              </div>}

            {/* Reviews */}
            {crypto.reviews.length > 0 && <div data-id="element-424">
                <h2 className="text-2xl font-bold mb-6" data-id="element-425">User Reviews</h2>
                <div className="space-y-4" data-id="element-426">
                  {crypto.reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-427" />)}
                </div>
              </div>}
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-428">
            <Card className="p-6" data-id="element-429">
              <h3 className="font-bold text-lg mb-4" data-id="element-430">Quick Stats</h3>
              <div className="space-y-4" data-id="element-431">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-432">
                  <span className="text-slate-600" data-id="element-433">Current Price</span>
                  <span className="font-semibold" data-id="element-434">
                    $
                    {crypto.price >= 1 ? crypto.price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) : crypto.price.toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-435">
                  <span className="text-slate-600" data-id="element-436">Market Cap Rank</span>
                  <span className="font-semibold" data-id="element-437">#{crypto.rank}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-438">
                  <span className="text-slate-600" data-id="element-439">24h Change</span>
                  <span className={`font-semibold ${crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-440">
                    {crypto.change24h >= 0 ? '+' : ''}
                    {crypto.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-441">
                  <span className="text-slate-600" data-id="element-442">Total Reviews</span>
                  <span className="font-semibold" data-id="element-443">{crypto.reviews.length}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-444">
              <h3 className="font-bold text-lg mb-2" data-id="element-445">Trust Score Breakdown</h3>
              <div className="text-3xl font-bold text-blue-600 mb-4" data-id="element-446">
                {crypto.trustScore.toFixed(1)}/10
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-447">
                Our trust score is calculated based on market cap, trading
                volume, community engagement, and historical performance.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}
// Helper function to calculate trust score
function calculateTrustScore(data: any): number {
  let score = 5.0;
  const marketCap = data.market_data?.market_cap?.usd || 0;
  if (marketCap > 100000000000) score += 2.0;else if (marketCap > 10000000000) score += 1.5;else if (marketCap > 1000000000) score += 1.0;else if (marketCap > 100000000) score += 0.5;
  const rank = data.market_cap_rank || 1000;
  if (rank <= 10) score += 1.5;else if (rank <= 50) score += 1.0;else if (rank <= 100) score += 0.5;
  const volume = data.market_data?.total_volume?.usd || 0;
  if (volume > 1000000000) score += 0.5;
  return Math.min(score, 10.0);
}
// Helper function to generate pros
function generatePros(data: any): string[] {
  const pros: string[] = [];
  const rank = data.market_cap_rank || 1000;
  if (rank <= 10) pros.push('Top 10 cryptocurrency by market cap');else if (rank <= 50) pros.push('Well-established in the market');
  const marketCap = data.market_data?.market_cap?.usd || 0;
  if (marketCap > 10000000000) pros.push('High market capitalization');
  const volume = data.market_data?.total_volume?.usd || 0;
  if (volume > 1000000000) pros.push('High trading volume and liquidity');
  if (data.links?.homepage?.[0]) pros.push('Active development and community');
  return pros;
}
// Helper function to generate cons
function generateCons(data: any): string[] {
  const cons: string[] = [];
  const change24h = data.market_data?.price_change_percentage_24h || 0;
  if (Math.abs(change24h) > 10) cons.push('High price volatility');
  const rank = data.market_cap_rank || 1000;
  if (rank > 100) cons.push('Lower market cap compared to top cryptocurrencies');
  const athChange = data.market_data?.ath_change_percentage?.usd || -100;
  if (athChange < -50) cons.push('Significantly below all-time high');
  return cons;
}